import React, { useState, useEffect } from 'react';
import {
  Card,
  Steps,
  Button,
  Alert,
  Typography,
  Space,
  Divider,
  Collapse,
  Tag,
  message,
  Spin,
} from 'antd';
import {
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MobileOutlined,
  SafetyOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import './IOSDownloadGuide.css';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;
const { Step } = Steps;

interface DeviceInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isWeChat: boolean;
  isSafari: boolean;
  version: string;
}

const IOSDownloadGuide: React.FC = () => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [installStatus, setInstallStatus] = useState<'idle' | 'downloading' | 'installing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // 下载链接配置（应该从后端获取或配置）
  const downloadConfig = {
    ios: process.env.REACT_APP_IOS_DOWNLOAD_URL || 'https://your-ios-download-url.com/app.ipa',
    android: process.env.REACT_APP_ANDROID_DOWNLOAD_URL || 'https://your-android-download-url.com/app.apk',
    qrCode: process.env.REACT_APP_QR_CODE_URL || '',
  };

  // 检测设备信息
  useEffect(() => {
    const detectDevice = () => {
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      const isAndroid = /Android/.test(ua);
      const isWeChat = /MicroMessenger/.test(ua);
      const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
      
      let version = '';
      if (isIOS) {
        const match = ua.match(/OS (\d+)_(\d+)/);
        if (match) {
          version = `${match[1]}.${match[2]}`;
        }
      }

      setDeviceInfo({
        isIOS,
        isAndroid,
        isWeChat,
        isSafari,
        version,
      });
    };

    detectDevice();
  }, []);

  // 处理下载
  const handleDownload = () => {
    if (!deviceInfo) return;

    if (deviceInfo.isWeChat) {
      message.warning('请在Safari浏览器中打开此页面进行下载');
      return;
    }

    if (deviceInfo.isIOS) {
      setInstallStatus('downloading');
      setCurrentStep(1);
      
      // iOS下载 - 直接打开下载链接
      const link = document.createElement('a');
      link.href = downloadConfig.ios;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 延迟检查安装状态
      setTimeout(() => {
        setInstallStatus('installing');
        setCurrentStep(2);
        message.info('请按照步骤完成安装和信任证书');
      }, 1000);
    } else if (deviceInfo.isAndroid) {
      // Android下载
      const link = document.createElement('a');
      link.href = downloadConfig.android;
      link.download = 'app.apk';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('开始下载Android版本');
    }
  };

  // 检查是否已安装（实际应该通过URL Scheme检测）
  const checkIfInstalled = (): boolean => {
    // 这里可以添加实际的检测逻辑
    // 例如：尝试打开app的URL Scheme
    return false;
  };

  // 重试安装
  const handleRetry = () => {
    setInstallStatus('idle');
    setCurrentStep(0);
    setErrorMessage('');
    handleDownload();
  };

  if (!deviceInfo) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>正在检测设备信息...</p>
      </div>
    );
  }

  return (
    <div className="ios-download-guide">
      <div className="guide-container">
        {/* 设备信息提示 */}
        <Card style={{ marginBottom: 24 }}>
          <Space>
            <MobileOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <Text strong>检测到您的设备：</Text>
              <Space style={{ marginLeft: 8 }}>
                {deviceInfo.isIOS && <Tag color="blue">iOS {deviceInfo.version}</Tag>}
                {deviceInfo.isAndroid && <Tag color="green">Android</Tag>}
                {deviceInfo.isWeChat && <Tag color="orange">微信浏览器</Tag>}
                {deviceInfo.isSafari && <Tag color="purple">Safari浏览器</Tag>}
              </Space>
            </div>
          </Space>
        </Card>

        {/* iOS超级签安装步骤 */}
        {deviceInfo.isIOS && (
          <>
            <Card title={<><SafetyOutlined /> iOS超级签安装指南</>} style={{ marginBottom: 24 }}>
              <Alert
                message="重要提示"
                description="iOS超级签名应用需要信任企业证书后才能正常使用。如果无法安装，请按照下方步骤操作。"
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Steps current={currentStep} direction="vertical" size="small">
                <Step
                  title="点击下载按钮"
                  description={
                    <div>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={handleDownload}
                        loading={installStatus === 'downloading' || installStatus === 'installing'}
                        disabled={installStatus === 'success'}
                        style={{ marginTop: 8 }}
                      >
                        {installStatus === 'idle' && '立即下载'}
                        {installStatus === 'downloading' && '正在下载...'}
                        {installStatus === 'installing' && '正在安装...'}
                        {installStatus === 'success' && '已安装'}
                      </Button>
                      {installStatus === 'error' && (
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={handleRetry}
                          style={{ marginLeft: 8 }}
                        >
                          重试
                        </Button>
                      )}
                    </div>
                  }
                />
                <Step
                  title="允许安装描述文件"
                  description={
                    <div>
                      <Paragraph>
                        下载完成后，系统会弹出提示：
                      </Paragraph>
                      <Alert
                        message="是否允许安装描述文件？"
                        description="请点击【允许】，然后按照提示完成安装。"
                        type="info"
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  }
                />
                <Step
                  title="信任企业证书（关键步骤）"
                  description={
                    <div>
                      <Paragraph strong style={{ color: '#ff4d4f' }}>
                        这是最关键的一步！如果不完成此步骤，应用将无法打开。
                      </Paragraph>
                      <ol style={{ paddingLeft: 20, marginTop: 12 }}>
                        <li>打开【设置】应用</li>
                        <li>进入【通用】</li>
                        <li>找到并点击【VPN与设备管理】或【设备管理】</li>
                        <li>找到企业级应用，点击进入</li>
                        <li>点击【信任 "企业名称"】</li>
                        <li>在弹出的确认框中点击【信任】</li>
                      </ol>
                      <Alert
                        message="找不到设备管理？"
                        description="如果iOS版本较新，可能显示为【VPN与设备管理】或【描述文件与设备管理】。"
                        type="info"
                        style={{ marginTop: 12 }}
                      />
                    </div>
                  }
                />
                <Step
                  title="完成安装"
                  description={
                    <div>
                      <Paragraph>
                        完成信任后，返回桌面，点击应用图标即可正常使用。
                      </Paragraph>
                      {installStatus === 'success' && (
                        <Alert
                          message="安装成功！"
                          description="您现在可以正常使用应用了。"
                          type="success"
                          showIcon
                          style={{ marginTop: 12 }}
                        />
                      )}
                    </div>
                  }
                />
              </Steps>

              {installStatus === 'error' && errorMessage && (
                <Alert
                  message="安装失败"
                  description={errorMessage}
                  type="error"
                  showIcon
                  style={{ marginTop: 24 }}
                />
              )}
            </Card>

            {/* 常见问题 */}
            <Card title={<><QuestionCircleOutlined /> 常见问题</>} style={{ marginBottom: 24 }}>
              <Collapse>
                <Panel header="为什么点击下载后没有反应？" key="1">
                  <Paragraph>
                    <strong>可能原因：</strong>
                  </Paragraph>
                  <ul>
                    <li>在微信中打开：请在Safari浏览器中打开此页面</li>
                    <li>网络问题：请检查网络连接</li>
                    <li>证书过期：请联系客服更新证书</li>
                  </ul>
                </Panel>
                <Panel header='提示"无法验证应用"怎么办？' key="2">
                  <Paragraph>
                    这是因为还没有信任企业证书。请按照上方步骤3完成信任操作。
                  </Paragraph>
                </Panel>
                <Panel header='找不到"设备管理"选项？' key="3">
                  <Paragraph>
                    不同iOS版本的名称可能不同：
                  </Paragraph>
                  <ul>
                    <li>iOS 14及以下：设置 → 通用 → 设备管理</li>
                    <li>iOS 15及以上：设置 → 通用 → VPN与设备管理</li>
                    <li>部分版本：设置 → 通用 → 描述文件与设备管理</li>
                  </ul>
                </Panel>
                <Panel header="安装后应用闪退？" key="4">
                  <Paragraph>
                    <strong>解决方法：</strong>
                  </Paragraph>
                  <ol>
                    <li>确认已完成信任企业证书的步骤</li>
                    <li>重启设备后再次尝试</li>
                    <li>删除应用后重新安装</li>
                    <li>联系客服检查证书状态</li>
                  </ol>
                </Panel>
                <Panel header="如何更新到新版本？" key="5">
                  <Paragraph>
                    新版本发布后，直接下载安装即可。如果遇到问题：
                  </Paragraph>
                  <ol>
                    <li>删除旧版本应用</li>
                    <li>重新下载新版本</li>
                    <li>重新完成信任证书步骤</li>
                  </ol>
                </Panel>
              </Collapse>
            </Card>

            {/* 备用下载方式 */}
            <Card title="备用下载方式" style={{ marginBottom: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message="如果上述方式无法下载"
                  description="您可以尝试以下备用方式："
                  type="info"
                />
                <div>
                  <Text strong>方式一：扫描二维码下载</Text>
                  <div style={{ marginTop: 8, textAlign: 'center' }}>
                    {downloadConfig.qrCode ? (
                      <img 
                        src={downloadConfig.qrCode} 
                        alt="下载二维码" 
                        style={{ 
                          width: 200, 
                          height: 200,
                          borderRadius: 8
                        }}
                      />
                    ) : (
                      <div style={{ 
                        width: 200, 
                        height: 200, 
                        background: '#f0f0f0', 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8
                      }}>
                        <Text type="secondary">请配置二维码图片URL</Text>
                      </div>
                    )}
                  </div>
                  <Paragraph type="secondary" style={{ marginTop: 8, textAlign: 'center' }}>
                    使用Safari浏览器扫描二维码下载
                  </Paragraph>
                </div>
                <Divider />
                <div>
                  <Text strong>方式二：联系客服获取下载链接</Text>
                  <Button type="link" style={{ marginLeft: 8 }}>
                    联系客服
                  </Button>
                </div>
              </Space>
            </Card>
          </>
        )}

        {/* Android下载 */}
        {deviceInfo.isAndroid && (
          <Card title="Android下载">
            <Alert
              message="Android设备"
              description="Android设备可以直接下载安装，无需特殊设置。"
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Button 
              type="primary" 
              icon={<DownloadOutlined />} 
              size="large"
              onClick={handleDownload}
            >
              下载Android版本
            </Button>
          </Card>
        )}

        {/* 微信浏览器提示 */}
        {deviceInfo.isWeChat && (
          <Card>
            <Alert
              message="检测到您在微信中打开"
              description={
                <div>
                  <Paragraph>
                    微信浏览器限制了下载功能，请按照以下步骤操作：
                  </Paragraph>
                  <ol>
                    <li>点击右上角【...】菜单</li>
                    <li>选择【在Safari中打开】</li>
                    <li>在Safari中完成下载和安装</li>
                  </ol>
                </div>
              }
              type="warning"
              showIcon
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default IOSDownloadGuide;

